/**
 * glass-shaders.js
 * ═════════════════════════════════════════════════════════════════════
 * Physically-based convex lens shader for the navigation glass overlay.
 *
 * Direct port of the LensClone shader from marijanapav.com/shop:
 *   - Oblate spheroid glass geometry
 *   - Full ray-tracing: front surface → glass interior → back surface → image plane
 *   - Per-channel IOR for chromatic aberration (Snell's law via GLSL refract)
 *   - Fresnel reflectance (Schlick approximation)
 *   - Specular highlights, rim glow, inner dimming
 *   - Bevel edge transition
 *
 * The canvas is sized to the nav bar. A cloned copy of the page
 * lives inside the canvas as a `layoutsubtree` child.
 * `texElementImage2D` captures the clone as a WebGL texture.
 *
 * @module glass-shaders
 * ═════════════════════════════════════════════════════════════════════
 */

// ─── Vertex Shader ─────────────────────────────────────────────────────────

export const NAV_GLASS_VERTEX = /* glsl */ `
attribute vec2 aPosition;
varying vec2 vQuadUV;

void main() {
  vQuadUV = aPosition * 0.5 + 0.5;
  gl_Position = vec4(aPosition, 0.0, 1.0);
}
`

// ─── Fragment Shader ───────────────────────────────────────────────────────
// Physically-based convex lens shader adapted for a rectangular nav bar.
//
// Key adaptation from the circular LensClone:
//   - Base UV computed directly from the nav's position in the source
//   - Lens coords scaled by max(navW, navH)/2 so the entire nav fits
//     within the unit disk for the spheroid math
//   - Rim/dim shading uses rectangular (Chebyshev) distance
//   - Full alpha output — CSS border-radius handles clipping

export const NAV_GLASS_FRAGMENT = /* glsl */ `
precision highp float;

varying vec2 vQuadUV;

uniform sampler2D uSourceTex;
uniform vec2  uSourceSizePx;
uniform vec2  uNavCenterPx;
uniform vec2  uNavSizePx;
uniform float uLensMagnification;
uniform float uLensIOR;
uniform float uAberrationAmount;
uniform float uLensThickness;
uniform float uBevelStartR;
uniform float uLensOblateness;
uniform float uRefractionDisplacementScale;

const float IOR_AIR = 1.0;
const float EPS = 1e-6;

// ── Ellipsoid front surface ─────────────────────────────────────────

void calcFrontSurfacePointAndNormal(
  vec2 lensCoords, float zScale,
  out vec3 frontPoint, out vec3 frontNormal
) {
  float radiusSq = dot(lensCoords, lensCoords);
  float safe = max(0.0, 1.0 - radiusSq);
  float zAxisDepth = zScale * sqrt(safe);

  frontPoint = vec3(lensCoords, zAxisDepth);
  frontNormal = normalize(vec3(
    lensCoords,
    zAxisDepth / (zScale * zScale + EPS)
  ));
}

// ── Ellipsoid back surface intersection ─────────────────────────────

bool intersectBackEllipsoid(
  vec3 rayOrigin, vec3 rayDir, float thickness, float zScale,
  out vec3 hitPoint
) {
  vec3 backCenter = vec3(0.0, 0.0, -thickness);
  vec3 oc = rayOrigin - backCenter;

  float zs2 = zScale * zScale;
  float a = dot(rayDir.xy, rayDir.xy) + (rayDir.z * rayDir.z) / zs2;
  float b = 2.0 * (dot(oc.xy, rayDir.xy) + (oc.z * rayDir.z) / zs2);
  float c = dot(oc.xy, oc.xy) + (oc.z * oc.z) / zs2 - 1.0;

  float disc = b * b - 4.0 * a * c;
  if (disc < 0.0) return false;

  float sq = sqrt(max(0.0, disc));
  float t1 = (-b - sq) / (2.0 * a);
  float t2 = (-b + sq) / (2.0 * a);

  float t = t1 > EPS ? t1 : t2;
  if (t <= 0.0) return false;

  hitPoint = rayOrigin + rayDir * t;
  return true;
}

vec3 calcBackEllipsoidNormal(vec3 pt, float thickness, float zScale) {
  vec3 bc = vec3(0.0, 0.0, -thickness);
  vec3 local = pt - bc;
  float zs2 = zScale * zScale;
  return normalize(vec3(local.xy, local.z / zs2));
}

// ── Full refraction trace through the lens ──────────────────────────

vec2 traceRefractionDispl(
  vec2 lensCoords, float ior, float zScale, float thickness
) {
  vec3 fp, fn;
  calcFrontSurfacePointAndNormal(lensCoords, zScale, fp, fn);

  vec3 inDir = vec3(0.0, 0.0, -1.0);
  vec3 glassDir = refract(inDir, fn, IOR_AIR / ior);
  if (length(glassDir) < EPS) return vec2(0.0);

  vec3 backPt;
  if (!intersectBackEllipsoid(fp, glassDir, thickness, zScale, backPt))
    return vec2(0.0);

  vec3 backN = calcBackEllipsoidNormal(backPt, thickness, zScale);
  vec3 outDir = refract(glassDir, -backN, ior / IOR_AIR);
  if (length(outDir) < EPS) return vec2(0.0);

  float t = -backPt.z / outDir.z;
  vec2 projected = backPt.xy + outDir.xy * t;

  return (projected - lensCoords) * uRefractionDisplacementScale;
}

// ── Shading normal ──────────────────────────────────────────────────

vec3 calcShadingNormal(vec2 lensCoords, float z) {
  return normalize(vec3(-lensCoords, z));
}

// ── Main ────────────────────────────────────────────────────────────

void main() {
  // ────────────────────────────────────────────────────────────────
  // Coordinate systems:
  //   vQuadUV:     GL convention — (0,0) = bottom-left, (1,1) = top-right
  //   Texture UV:  DOM convention — (0,0) = top-left, (1,1) = bottom-right
  //   Source px:   DOM convention — X right, Y down
  //   Lens coords: visual space  — X right, Y up (for spheroid math)
  // ────────────────────────────────────────────────────────────────

  // Flip vQuadUV.y to get texture-space UV for this fragment
  vec2 texUV = vec2(vQuadUV.x, 1.0 - vQuadUV.y);

  // Offset in source pixels from nav center (DOM coords, Y down)
  vec2 offsetPx = (texUV - 0.5) * uNavSizePx;
  vec2 pixelInSource = uNavCenterPx + offsetPx;

  vec2 invSrcSize = 1.0 / uSourceSizePx;
  vec2 centerUV = uNavCenterPx * invSrcSize;
  vec2 baseUV = pixelInSource * invSrcSize;

  // Magnification: zoom toward nav center
  vec2 magnifiedUV = centerUV + (baseUV - centerUV) / max(EPS, uLensMagnification);

  // ── Lens geometry coordinates (visual space: X right, Y up) ───────
  // Convert from texUV to centered visual coords
  vec2 visualPos = vec2(texUV.x - 0.5, 0.5 - texUV.y) * uNavSizePx;
  float maxHalf = max(uNavSizePx.x, uNavSizePx.y) * 0.5;
  vec2 lensCoords = visualPos / maxHalf;
  float radiusSq = dot(lensCoords, lensCoords);
  float radius = sqrt(radiusSq);

  float zScale = clamp(uLensOblateness, 0.2, 1.0);

  // ── Per-channel IOR for chromatic aberration ──────────────────────
  float iorR = uLensIOR - uAberrationAmount;
  float iorG = uLensIOR;
  float iorB = uLensIOR + uAberrationAmount;

  // Trace refraction (displacement in visual lens-unit coords)
  vec2 displR = traceRefractionDispl(lensCoords, iorR, zScale, uLensThickness);
  vec2 displG = traceRefractionDispl(lensCoords, iorG, zScale, uLensThickness);
  vec2 displB = traceRefractionDispl(lensCoords, iorB, zScale, uLensThickness);

  // Convert displacement from visual lens units to texture UV offset.
  // Visual +X = source +X = UV +u
  // Visual +Y = source -Y = UV -v (Y flipped between visual and DOM)
  vec2 toUV = vec2(1.0, -1.0) * maxHalf * invSrcSize;
  vec2 displUVR = displR * toUV;
  vec2 displUVG = displG * toUV;
  vec2 displUVB = displB * toUV;

  // Bevel attenuation at edges
  float rimFeather = smoothstep(uBevelStartR, 1.0, radius);
  float edgeAtten = 1.0 - rimFeather * 0.12;

  // Final UV per channel = magnified base + refraction displacement
  vec2 uvR = magnifiedUV + displUVR * edgeAtten;
  vec2 uvG = magnifiedUV + displUVG * edgeAtten;
  vec2 uvB = magnifiedUV + displUVB * edgeAtten;

  // Sample per-channel
  vec3 refractedCol = vec3(
    texture2D(uSourceTex, uvR).r,
    texture2D(uSourceTex, uvG).g,
    texture2D(uSourceTex, uvB).b
  );

  // ── Shading ───────────────────────────────────────────────────────
  float zDepth = sqrt(max(0.0, 1.0 - min(radiusSq, 1.0)));
  vec3 normal = calcShadingNormal(lensCoords, zDepth);
  vec3 viewDir = vec3(0.0, 0.0, 1.0);
  vec3 lightDir = normalize(vec3(0.35, 0.55, 1.0));

  // Fresnel (Schlick)
  float r0 = pow((uLensIOR - 1.0) / (uLensIOR + 1.0), 2.0);
  float vdn = clamp(dot(viewDir, normal), 0.0, 1.0);
  float fresnel = r0 + (1.0 - r0) * pow(1.0 - vdn, 5.0);

  // Specular
  vec3 halfV = normalize(viewDir + lightDir);
  float spec = pow(max(dot(normal, halfV), 0.0), 80.0) * 0.35;

  // Rim + inner dim use rectangular distance (Chebyshev) so the
  // effect follows the nav bar shape instead of a circle.
  vec2 normDist = abs(texUV * 2.0 - 1.0);
  float rectDist = max(normDist.x, normDist.y);

  float rimBoost = smoothstep(0.78, 0.98, rectDist) * 0.5;
  float innerDim = smoothstep(0.55, 1.0, rectDist) * 0.18;

  // Composite
  vec3 highlights = vec3(spec * fresnel) + vec3(rimBoost * fresnel);
  vec3 shaded = refractedCol * (1.0 - innerDim) + highlights;

  // Full alpha — CSS border-radius handles nav shape clipping
  gl_FragColor = vec4(shaded, 1.0);
}
`

// ─── Default Lens Parameters (same as LensClone) ──────────────────────────

export const NAV_GLASS_DEFAULTS = {
  magnification: 1.0,
  ior: 1.52,
  aberration: 0.01,
  thickness: 0.9,
  bevelStart: 0.9,
  oblateness: 0.65,
  refractionDisplacement: 0.6,
}
